import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { passkeyAddBegin, passkeyAddComplete } from '../api/auth'
import { useAuthStore } from '../store/auth'

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (const byte of bytes) str += String.fromCharCode(byte)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

function base64urlToBuffer(b64: string): ArrayBuffer {
  const str = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
  const buf = new ArrayBuffer(str.length)
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i)
  return buf
}

function encodeCredentialForServer(credential: PublicKeyCredential): unknown {
  const resp = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      attestationObject: bufferToBase64url(resp.attestationObject),
      transports: resp.getTransports(),
    },
    type: credential.type,
  }
}

export function AddPasskey() {
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const setAuth = useAuthStore((s) => s.setAuth)
  const navigate = useNavigate()

  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')

  if (!token) {
    return (
      <div style={{ padding: '2rem', maxWidth: 480 }}>
        <h2>add passkey</h2>
        <p style={{ color: 'var(--color-1)' }}>no token found in the url</p>
      </div>
    )
  }

  async function handleAdd() {
    if (!token) return
    setStatus('working')
    setErrorMessage('')
    try {
      const options = await passkeyAddBegin(token) as PublicKeyCredentialCreationOptions

      const rawExclude = options.excludeCredentials as unknown as Array<{ id: string; type: string; transports?: string[] }> | undefined
      const decodedOptions: PublicKeyCredentialCreationOptions = {
        ...options,
        challenge: base64urlToBuffer((options.challenge as unknown as string)),
        user: {
          ...(options.user as PublicKeyCredentialUserEntity),
          id: base64urlToBuffer((options.user as unknown as { id: string }).id),
        },
        excludeCredentials: rawExclude?.map((c) => ({ id: base64urlToBuffer(c.id), type: c.type as PublicKeyCredentialType, transports: c.transports as AuthenticatorTransport[] | undefined })),
      }

      const credential = await navigator.credentials.create({ publicKey: decodedOptions }) as PublicKeyCredential
      const result = await passkeyAddComplete(token, encodeCredentialForServer(credential))
      setAuth(result.token, result.user_id, result.username)
      navigate(`/~${result.username}/journal`, { replace: true })
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'failed to add passkey')
      setStatus('error')
    }
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 480 }}>
      <h2>add passkey</h2>
      {status === 'idle' && (
        <>
          <p>click the button below to register a new passkey for your account.</p>
          <button type="button" onClick={handleAdd}>add passkey</button>
        </>
      )}
      {status === 'working' && <p>follow your browser's passkey prompts…</p>}
      {status === 'error' && (
        <>
          <p style={{ color: 'var(--color-1)' }}>{errorMessage || 'something went wrong'}</p>
          <button type="button" onClick={() => setStatus('idle')}>try again</button>
        </>
      )}
    </div>
  )
}
